import { RadarChart } from '../shared/components/dashboard/RadarChart';
import { InvestorCard } from '../shared/components/dashboard/InvestorCard';
import { famousInvestors } from '../shared/data/famousInvestors';
import { BarChart3, MessageSquare, Zap, Loader2 } from 'lucide-react';
import { usePersonaStats } from '../shared/hooks/usePersonaStats';
import { personaService, PersonaService } from '../shared/services/personaService';
type DashboardOverviewPageProps = {
  service?: PersonaService;
};

export function DashboardOverviewPage({ service = personaService }: DashboardOverviewPageProps) {
  const { stats, loading } = usePersonaStats(service);

  // Use default values if no data yet
  const firmProfile = stats ? {
    macroVsMicro: stats.aggregateTraits.macroVsMicro,
    valueVsGrowth: stats.aggregateTraits.valueVsGrowth,
    timeHorizon: stats.aggregateTraits.timeHorizon,
    passiveVsActive: stats.aggregateTraits.passiveVsActive,
    strategicVsFinancial: stats.aggregateTraits.strategicVsFinancial,
  } : {
    macroVsMicro: 0,
    valueVsGrowth: 0,
    timeHorizon: 0,
    passiveVsActive: 0,
    strategicVsFinancial: 0,
  };

  const calculateMatch = (investorTraits: typeof firmProfile) => {
    const sum = Object.values(firmProfile).reduce((acc, val, idx) => {
      const diff = Math.abs(val - Object.values(investorTraits)[idx]);
      return acc + diff;
    }, 0);
    return Math.max(0, 100 - (sum / 5));
  };

  const matches = famousInvestors.map((investor) => ({
    ...investor,
    matchPercentage: calculateMatch(investor.traits),
  })).sort((a, b) => b.matchPercentage - a.matchPercentage);

  const traitLabels = [
    { key: 'macroVsMicro' as const, label: 'Orientation', leftLabel: 'Micro', rightLabel: 'Macro' },
    { key: 'valueVsGrowth' as const, label: 'Investment Bias', leftLabel: 'Value', rightLabel: 'Growth' },
    { key: 'timeHorizon' as const, label: 'Time Horizon', leftLabel: 'Short-term', rightLabel: 'Long-term' },
    { key: 'passiveVsActive' as const, label: 'Engagement Level', leftLabel: 'Passive', rightLabel: 'Active' },
    { key: 'strategicVsFinancial' as const, label: 'Investment Approach', leftLabel: 'Financial', rightLabel: 'Strategic' },
  ];

  // Show loading spinner
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#291B4C] animate-spin mx-auto mb-4" strokeWidth={2} />
          <p className="text-lg font-semibold text-[#291B4C]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Always show the dashboard, even with zero values
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1400px] mx-auto px-12 py-12 space-y-12">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-[#291B4C] tracking-tight uppercase">
            Investment Persona Analytics
          </h1>
          <p className="text-base text-[#291B4C]/60 tracking-wide">
            Comprehensive analysis of your team's investment philosophy
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-6">
          <StatCard
            title="Total Submissions"
            value={stats?.totalSubmissions || 0}
            icon={<BarChart3 className="w-6 h-6" strokeWidth={2} />}
          />
          <StatCard
            title="Avg Questions Asked"
            value={(stats?.avgQuestionsAsked || 0).toFixed(1)}
            icon={<MessageSquare className="w-6 h-6" strokeWidth={2} />}
          />
          <StatCard
            title="Processing Status"
            value={`${stats?.processedCount || 0}/${stats?.totalSubmissions || 0}`}
            icon={<Zap className="w-6 h-6" strokeWidth={2} />}
          />
        </div>

        {/* Firm Profile */}
        <div className="border border-[#291B4C]/10 rounded-2xl p-10 bg-white">
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-[#291B4C] uppercase tracking-tight mb-2">
              Firm Investment Profile
            </h2>
            <p className="text-sm text-[#291B4C]/60 tracking-wide">
              Aggregated investment philosophy across all portfolio segments
            </p>
          </div>

          <div className="grid grid-cols-5 gap-16">
            <div className="col-span-2 flex items-center justify-center">
              <RadarChart data={firmProfile} color="#291B4C" size={380} />
            </div>

            <div className="col-span-3 space-y-8">
              {traitLabels.map((trait) => (
                <div key={trait.key}>
                  <div className="flex justify-between items-baseline mb-3">
                    <span className="text-sm font-semibold text-[#291B4C] uppercase tracking-wider">
                      {trait.label}
                    </span>
                    <span className="text-2xl font-bold text-[#291B4C] tabular-nums">
                      {firmProfile[trait.key]}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-[#291B4C]/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#291B4C] rounded-full"
                        style={{ width: `${firmProfile[trait.key]}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-[#291B4C]/50 font-semibold uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        <span>←</span>
                        <span>{trait.leftLabel}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>{trait.rightLabel}</span>
                        <span>→</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Investor Comparison */}
        <div>
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[#291B4C] uppercase tracking-tight mb-2">
              Investment Style Comparison
            </h2>
            <p className="text-sm text-[#291B4C]/60 tracking-wide">
              How your firm compares to legendary investors
            </p>
          </div>
          
          <div className="grid grid-cols-4 gap-6">
            {matches.map((investor, idx) => (
              <InvestorCard
                key={investor.name}
                name={investor.name}
                description={investor.description}
                photo={investor.photo}
                matchPercentage={investor.matchPercentage}
                rank={idx === 0 ? 1 : undefined}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon }: { title: string; value: string | number; icon: React.ReactNode }) {
  return (
    <div className="border border-[#291B4C]/10 rounded-xl p-6 bg-white hover:border-[#291B4C]/20">
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-lg bg-[#291B4C] flex items-center justify-center text-white flex-shrink-0">
          {icon}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-[#291B4C]/50 uppercase tracking-widest mb-2">
          {title}
        </p>
        <p className="text-3xl font-bold text-[#291B4C] tabular-nums">
          {value}
        </p>
      </div>
    </div>
  );
}
